import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { Observer } from "gsap/Observer";
import { api } from "@/lib/api";
import ScrambleText from "@/components/ScrambleText";

gsap.registerPlugin(Observer);

const DIN = '"Figtree", "Inter", system-ui, sans-serif';
const ACTIVICA = '"FT Activica Normal", "Outfit", sans-serif';
const ACTIVICA_PIXEL = '"FT Activica Pixel", "FT Activica Normal", monospace';

// Card constants
const CARD_W = 200;
const CARD_H = 260;
const CARD_RADIUS = 14;
const IMG_INSET = 8;
const IMG_RADIUS = 38;
const LABEL_BAND_H = 44;

// 3 frame colors cycle around the orbit
const FRAME_PALETTE = [
    {
        // Orange / coral
        bg: "#E85A2F",
        labelColor: "#FFFCE1",
    },
    {
        // Lime → yellow-green gradient
        bg: "linear-gradient(180deg, #A8E852 0%, #80E830 100%)",
        labelColor: "#0A0A0A",
    },
    {
        // Cyan → blue gradient
        bg: "linear-gradient(180deg, #4FC3F7 0%, #2196F3 100%)",
        labelColor: "#FFFCE1",
    },
];

// Pre-randomised but stable per-slot tilt (degrees)
const TILTS = [-6, 5, -3, 8, -7, 4, -5, 6, -4, 7];

export default function OrbitalCities() {
    const sceneRef = useRef(null);
    const stateRef = useRef({
        baseAngle: 0,
        sharedBoost: 0,
        tiles: [],
        rx: 0,
        ry: 0,
        cx: 0,
        cy: 0,
    });
    const [cards, setCards] = useState([]);

    // Fetch city cards once
    useEffect(() => {
        let mounted = true;
        api.get("/city-cards")
            .then((r) => {
                if (!mounted) return;
                const sorted = [...r.data].sort(
                    (a, b) => a.slot_index - b.slot_index,
                );
                setCards(sorted);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    // Init orbital animation once cards are loaded
    useEffect(() => {
        if (cards.length === 0) return;
        const scene = sceneRef.current;
        if (!scene) return;

        const N = cards.length;
        const tileEls = scene.querySelectorAll("[data-orbital-card]");
        const st = stateRef.current;

        // Initialize tile records
        st.tiles = Array.from(tileEls).map((el, i) => ({
            el,
            angle: (i / N) * Math.PI * 2,
            tilt: TILTS[i % TILTS.length],
            scatterA: 0,
            scatterR: 0,
            scatterVA: 0,
            scatterVR: 0,
            friction: 0.91 + Math.random() * 0.04,
        }));

        const computeRadii = () => {
            const rect = scene.getBoundingClientRect();
            st.cx = rect.width / 2;
            st.cy = rect.height / 2;
            st.baseRX = rect.width * 0.44;
            st.baseRY = rect.height * 0.46;
        };
        computeRadii();
        const onResize = () => computeRadii();
        window.addEventListener("resize", onResize);

        const tick = () => {
            st.baseAngle += 0.0018;
            const cx = st.cx;
            const cy = st.cy;

            for (const tile of st.tiles) {
                tile.scatterA += tile.scatterVA;
                tile.scatterR += tile.scatterVR;
                tile.scatterVA *= tile.friction;
                tile.scatterVR *= tile.friction;
                tile.scatterA *= tile.friction;
                tile.scatterR *= tile.friction;

                const a = st.baseAngle + tile.angle + tile.scatterA;
                const rx = st.baseRX + st.sharedBoost + tile.scatterR;
                const ry = rx * 0.65;
                const px = cx + Math.cos(a) * rx - CARD_W / 2;
                const py = cy + Math.sin(a) * ry - CARD_H / 2;

                const depth = (Math.sin(a) + 1) / 2;
                const scale = 0.55 + depth * (0.95 - 0.55);
                const opacity = 0.4 + depth * (1.0 - 0.4);
                const zIndex = Math.round(depth * 1000);

                gsap.set(tile.el, {
                    x: px,
                    y: py,
                    scale,
                    opacity,
                    rotation: tile.tilt,
                    zIndex,
                });
            }

            st.sharedBoost *= 0.88;
        };
        gsap.ticker.add(tick);

        const observer = Observer.create({
            target: scene,
            type: "wheel,touch",
            wheelSpeed: 1,
            preventDefault: false,
            onWheel: (self) => {
                const dy = self.deltaY;
                st.sharedBoost += dy * 0.035;
                for (const tile of st.tiles) {
                    tile.scatterVR += dy * (0.025 + Math.random() * 0.035);
                    tile.scatterVA += dy * (Math.random() - 0.5) * 0.0001;
                }
            },
            onDrag: (self) => {
                const dx = self.deltaX;
                st.sharedBoost += dx * 0.035;
                for (const tile of st.tiles) {
                    tile.scatterVR += dx * (0.025 + Math.random() * 0.035);
                    tile.scatterVA += dx * (Math.random() - 0.5) * 0.0001;
                }
            },
        });

        return () => {
            gsap.ticker.remove(tick);
            observer.kill();
            window.removeEventListener("resize", onResize);
        };
    }, [cards]);

    return (
        <section
            id="community"
            data-testid="cities-section"
            className="relative py-24 md:py-32 border-t border-line overflow-hidden"
            style={{ background: "#0a0a0a", isolation: "isolate" }}
        >
            <div
                ref={sceneRef}
                data-testid="orbital-scene"
                className="relative w-full"
                style={{
                    height: "880px",
                    touchAction: "pan-y",
                }}
            >
                {/* Central pixel heading - "{ CITY CHAPTERS }" */}
                <div
                    aria-hidden
                    className="absolute inset-0 grid place-items-center pointer-events-none select-none"
                    style={{ zIndex: 500 }}
                >
                    <div className="flex flex-col items-center gap-8 md:gap-10 pointer-events-none">
                    <h2
                        data-testid="cities-heading"
                        className="flex items-center justify-center gap-3 md:gap-6"
                        style={{
                            fontFamily: ACTIVICA_PIXEL,
                            fontWeight: 400,
                        }}
                    >
                        <span
                            aria-hidden
                            style={{
                                fontSize: "clamp(3rem, 7vw, 6.5rem)",
                                fontFamily: ACTIVICA,
                                fontWeight: 300,
                                color: "#FFFCE1",
                                lineHeight: 1,
                            }}
                        >
                            {"{"}
                        </span>
                        <div className="flex flex-col items-center text-center leading-[1.05]">
                <ScrambleText
                    text="Kicking Off In"
                    className="text-[#FFFCE1] uppercase block"
                    style={{
                        fontSize: "clamp(2rem, 5vw, 4.6rem)",
                        letterSpacing: "0.04em",
                    }}
                />
                <ScrambleText
                    text="10 Cities."
                    className="uppercase block"
                    style={{
                        fontSize: "clamp(2rem, 5vw, 4.6rem)",
                        letterSpacing: "0.04em",
                        background: "linear-gradient(180deg, #90FDA6 0%, #F8FF28 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        color: "transparent",
                    }}
                />
            </div>
                        <span
                            aria-hidden
                            style={{
                                fontSize: "clamp(3rem, 7vw, 6.5rem)",
                                fontFamily: ACTIVICA,
                                fontWeight: 300,
                                color: "#FFFCE1",
                                lineHeight: 1,
                            }}
                        >
                            {"}"}
                        </span>
                    </h2>

                    {/* Apply Now CTA - matches hero button */}
                    <a
                        href="/apply"
                        data-testid="cities-cta-apply"
                        className="inline-flex items-center justify-center hover:brightness-110 transition-[filter] pointer-events-auto"
                        style={{
                            padding: "9.2335px 23.0837px",
                            gap: "9.23px",
                            minWidth: "253px",
                            height: "46.47px",
                            background:
                                "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                            boxShadow:
                                "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                            borderRadius: "11.5px",
                            fontFamily:
                                '"Figtree", "Inter", system-ui, sans-serif',
                            fontWeight: 700,
                            fontSize: "16.1px",
                            lineHeight: "28px",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#0E100F",
                            textDecoration: "none",
                        }}
                    >
                        Lead a Chapter
                    </a>
                    </div>
                </div>

                {/* Cards */}
                {cards.map((c, i) => (
                    <OrbitalCard key={c.id || i} card={c} index={i} />
                ))}
            </div>
        </section>
    );
}

function OrbitalCard({ card, index }) {
    const hasImage = !!card.image_url;
    const src = card.image_url ? resolveSrc(card.image_url) : null;
    const palette = FRAME_PALETTE[index % FRAME_PALETTE.length];
    return (
        <div
            data-orbital-card
            data-testid={`orbital-card-${index}`}
            className="absolute top-0 left-0 will-change-transform"
            style={{
                width: CARD_W,
                height: CARD_H,
                borderRadius: CARD_RADIUS,
                background: palette.bg,
                boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
                padding: IMG_INSET,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    borderRadius: IMG_RADIUS,
                    overflow: "hidden",
                    background: "rgba(0,0,0,0.18)",
                    display: "grid",
                    placeItems: "center",
                }}
            >
                {hasImage ? (
                    <img
                        src={src}
                        alt={card.city_name}
                        loading="lazy"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                ) : (
                    <span
                        style={{
                            fontFamily:
                                '"FT Activica Strong", "FT Activica Normal", sans-serif',
                            fontWeight: 800,
                            color: "rgba(255,255,255,0.35)",
                            fontSize: "3.4rem",
                            lineHeight: 1,
                        }}
                    >
                        {String(card.slot_index + 1).padStart(2, "0")}
                    </span>
                )}
            </div>
            <div
                style={{
                    height: LABEL_BAND_H,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: palette.labelColor,
                    fontFamily:
                        '"FT Activica Pixel", "FT Activica Normal", monospace',
                    fontWeight: 400,
                    fontSize: 22,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    paddingTop: 8,
                    lineHeight: 1,
                }}
            >
                {card.city_name}
            </div>
        </div>
    );
}

function resolveSrc(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // Same-origin relative URL: works on both preview and production
    // (ingress routes `/api/*` to the backend on whichever domain
    // the user is browsing).
    if (url.startsWith("/")) {
        return `${window.location.origin}${url}`;
    }
    return url;
}
